import { Skeleton } from "@components/wrapper";
import { useIsShopsite } from "@context/DomainContext";
import { joinClassNames } from "@utils/helpers";
import CartFee from "./cartFee";

const SkeletonLoading = () => {
  const isShopsite = useIsShopsite();

  return (
    <>
      <div className="hidden sm:block">
        <div className="relative flex items-center sm:items-start h-40">
          <Skeleton
            sharedClasses="h-20 w-20 flex-shrink-0 overflow-hidden sm:h-40 sm:w-40 animate-pulse"
            whitelabelClasses="bg-[color:var(--gray-a4)] rounded-[var(--radius-3)]"
            shopsiteClasses="bg-gray-200 rounded-lg"
          />
          <div className="ml-6 flex flex-col justify-between w-full h-full">
            <div className="flex justify-between">
              <Skeleton
                sharedClasses="h-9 animate-pulse w-1/4"
                whitelabelClasses="bg-[color:var(--gray-a7)] rounded-[var(--radius-1)]"
                shopsiteClasses="bg-gray-500 rounded"
              />
              <Skeleton
                sharedClasses="h-9 animate-pulse mt-2 w-1/6"
                whitelabelClasses="bg-[color:var(--gray-a7)] rounded-[var(--radius-1)]"
                shopsiteClasses="bg-gray-500 rounded"
              />
            </div>
            <div className="mt-auto">
              <div className="flex mt-2 items-center gap-1.5">
                <Skeleton
                  sharedClasses="h-5 animate-pulse w-5 shrink-0"
                  whitelabelClasses="bg-[color:var(--gray-a5)] rounded-[var(--radius-1)]"
                  shopsiteClasses="bg-gray-400 rounded"
                />
                <Skeleton
                  sharedClasses="h-5 animate-pulse w-1/5"
                  whitelabelClasses="bg-[color:var(--gray-a5)] rounded-[var(--radius-1)]"
                  shopsiteClasses="bg-gray-400 rounded"
                />
              </div>
              <div className="flex mt-2 items-center gap-1.5">
                <Skeleton
                  sharedClasses="h-5 animate-pulse w-5 shrink-0"
                  whitelabelClasses="bg-[color:var(--gray-a5)] rounded-[var(--radius-1)]"
                  shopsiteClasses="bg-gray-400 rounded"
                />
                <Skeleton
                  sharedClasses="h-5 animate-pulse w-1/6"
                  whitelabelClasses="bg-[color:var(--gray-a5)] rounded-[var(--radius-1)]"
                  shopsiteClasses="bg-gray-400 rounded"
                />
              </div>
            </div>
          </div>
        </div>
        <div
          className={joinClassNames(
            "mt-4 w-full flex flex-col border",
            isShopsite
              ? "rounded-lg"
              : "rounded-[var(--radius-3)] border-[color:var(--gray-5)]"
          )}
        >
          <div
            className={joinClassNames(
              "flex items-start p-4 border-b",
              !isShopsite ? "border-[color:var(--gray-5)]" : ""
            )}
          >
            <div className="flex-1">
              <Skeleton
                sharedClasses="h-5 w-1/3"
                whitelabelClasses="!bg-[color:var(--gray-a7)] rounded-[var(--radius-1)]"
                shopsiteClasses="!bg-gray-500 rounded"
              />
            </div>
            <div className="flex-1">
              <Skeleton
                sharedClasses="h-5 w-1/5"
                whitelabelClasses="!bg-[color:var(--gray-a7)] rounded-[var(--radius-1)]"
                shopsiteClasses="!bg-gray-500 rounded"
              />
            </div>
            <div className="flex-1 mr-6">
              <Skeleton
                sharedClasses="h-5 w-1/5"
                whitelabelClasses="!bg-[color:var(--gray-a7)] rounded-[var(--radius-1)]"
                shopsiteClasses="!bg-gray-500 rounded"
              />
            </div>
          </div>
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={`${index}-first`}
              className={joinClassNames(
                "p-4 flex items-center justify-between animate-pulse",
                index < 2 ? "border-b" : "",
                !isShopsite ? "border-[color:var(--gray-5)]" : ""
              )}
            >
              <div className="flex flex-col gap-1 flex-1">
                <Skeleton
                  sharedClasses="h-5 w-3/5"
                  whitelabelClasses="!bg-[color:var(--gray-a7)] rounded-[var(--radius-1)]"
                  shopsiteClasses="!bg-gray-500 rounded"
                />
                <Skeleton
                  sharedClasses="h-7 w-1/2"
                  whitelabelClasses="!bg-[color:var(--gray-a5)] rounded-[var(--radius-1)]"
                  shopsiteClasses="!bg-gray-400 rounded"
                />
              </div>
              <div className="flex-1">
                <Skeleton
                  sharedClasses="h-5 w-1/4"
                  whitelabelClasses="!bg-[color:var(--gray-a5)] rounded-[var(--radius-1)]"
                  shopsiteClasses="!bg-gray-400 rounded"
                />
              </div>
              <div className="flex items-center justify-between flex-1 mr-[calc(1.5rem+1px)]">
                <Skeleton
                  sharedClasses="h-8 w-[12.75rem]"
                  whitelabelClasses="!bg-[color:var(--gray-a5)] rounded-[var(--radius-3)]"
                  shopsiteClasses="!bg-gray-400 rounded-lg"
                />
                <Skeleton
                  sharedClasses="h-6 w-6"
                  whitelabelClasses="!bg-[color:var(--gray-a5)] rounded-[var(--radius-1)]"
                  shopsiteClasses="!bg-gray-400 rounded"
                />
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-center mt-10">
          <div className="w-full max-w-2xl">
            <CartFee checkoutDetails={undefined} isLoading />
            <div className="flex items-center justify-between w-full">
              <Skeleton
                sharedClasses="h-5 w-32 mt-6"
                whitelabelClasses="!bg-[color:var(--gray-a5)] rounded-[var(--radius-1)]"
                shopsiteClasses="!bg-gray-400 rounded"
              />
              <div className="flex items-center mt-6 gap-2">
                <Skeleton
                  sharedClasses="h-10 w-[110px]"
                  whitelabelClasses="!bg-[color:var(--gray-a5)] rounded-[var(--radius-2)]"
                  shopsiteClasses="!bg-gray-400 rounded-md"
                />
                <Skeleton
                  sharedClasses="h-10 w-[101px]"
                  whitelabelClasses="!bg-[color:var(--gray-a5)] rounded-[var(--radius-2)]"
                  shopsiteClasses="!bg-gray-400 rounded-md"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col sm:hidden gap-2 items-start rounded-lg">
        <div className="flex w-full items-center justify-between">
          <Skeleton
            sharedClasses="mt-2 h-7 w-24 rounded-md"
            whitelabelClasses="bg-[color:var(--gray-a7)]"
            shopsiteClasses="bg-gray-500"
          />
          <Skeleton
            sharedClasses="mt-2 h-6 w-24 rounded-md"
            whitelabelClasses="bg-[color:var(--gray-a7)]"
            shopsiteClasses="bg-gray-500"
          />
        </div>
        <Skeleton
          sharedClasses="mt-1 h-5 w-52 rounded-md"
          whitelabelClasses="bg-[color:var(--gray-a5)]"
          shopsiteClasses="bg-gray-400"
        />
        <Skeleton
          sharedClasses="mt-1 h-5 w-52 rounded-md"
          whitelabelClasses="bg-[color:var(--gray-a5)]"
          shopsiteClasses="bg-gray-400"
        />
      </div>
      <div className="sm:hidden mt-4 flex flex-col gap-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={`${index}-second`}
            className={joinClassNames(
              "flex flex-wrap items-center justify-between h-36 gap-x-6 gap-y-3 py-2 px-4 animate-pulse border rounded-xl",
              isShopsite
                ? "bg-gray-200"
                : "bg-[color:var(--gray-a4)] border-[color:var(--gray-a6)]"
            )}
          />
        ))}
      </div>
    </>
  );
};

export default SkeletonLoading;
